import React from 'react';

import { FormattedMessage } from 'react-intl';

import AvatarGroup, { AvatarProps } from '@atlaskit/avatar-group';
import AddIcon from '@atlaskit/icon/glyph/add';
import EditorDividerIcon from '@atlaskit/icon/glyph/editor/divider';
import { Box, Inline, Stack } from '@atlaskit/primitives';

import { ActivityApi, ApiUserField } from 'src/components/activity/types';
import { User } from 'src/components/types';

import { messages } from './pull-request-activity-events.i18n';

type Props = {
  event: ActivityApi['Update'];
};

export const PullRequestReviewersAddedEntry = ({ event }: Props) => {
  const users: User[] = event.update.changes.reviewers?.added
    ? event.update.changes.reviewers.added
    : [];
  const reviewerInfo = {
    numReviewers: users.length,
    otherReviewers: users.length - 1,
    reviewer: users[0].display_name,
    reviewer2: users.length === 2 ? users[1].display_name : '',
  };
  return (
    <FormattedMessage
      values={reviewerInfo}
      {...messages.reviewersAddedMessage}
    />
  );
};

export const PullRequestReviewersAddedTitle = ({
  event,
}: Props): JSX.Element => {
  const added = event.update.changes.reviewers?.added?.length;
  const removed = event.update.changes.reviewers?.removed?.length;
  if (added && !removed) {
    return (
      <FormattedMessage values={{ added }} {...messages.reviewersAddedTitle} />
    );
  } else if (removed && !added) {
    return (
      <FormattedMessage
        values={{ removed }}
        {...messages.reviewersRemovedTitle}
      />
    );
  } else if (added && removed) {
    return (
      <FormattedMessage
        values={{ added, removed }}
        {...messages.reviewersAddedAndRemovedTitle}
      />
    );
  } else {
    // this shouldn't ever happen
    return <></>;
  }
};

const transformReviewers = (
  reviewers: ApiUserField[] | undefined
): AvatarProps[] =>
  reviewers?.map((reviewer, index) => ({
    key: index,
    name: reviewer.display_name,
    src: reviewer.links.avatar.href,
  })) || [];

export const PullRequestReviewersAddedBody = ({ event }: Props) => {
  const added = event.update.changes.reviewers?.added?.length;
  const removed = event.update.changes.reviewers?.removed?.length;

  return (
    <Box>
      <Stack>
        {added && added > 0 ? (
          <Inline alignBlock="center">
            <Box padding="space.200"></Box>
            <AddIcon label="reviewers added" />
            <Box padding="space.050"></Box>
            <AvatarGroup
              appearance="stack"
              data={transformReviewers(event.update.changes.reviewers?.added)}
            />
          </Inline>
        ) : (
          <></>
        )}
        {removed && removed > 0 ? (
          <Inline alignBlock="center">
            <Box padding="space.200"></Box>
            <EditorDividerIcon label="reviewers removed" />
            <Box padding="space.050"></Box>
            <AvatarGroup
              appearance="stack"
              data={transformReviewers(event.update.changes.reviewers?.removed)}
            />
          </Inline>
        ) : (
          <></>
        )}
      </Stack>
    </Box>
  );
};
